import { v4 as uuidv4 } from 'uuid';
import { getCurrentCustomer } from '../../../../lib/currentCustomer';
import { GET_ASSETS_DEFAULT_XGAC_FILTER } from './getAssetsDefaults';
import { ensureMinScheduleTasks } from './scheduleTaskDefaults';
import { ScheduleTaskForm, TaskTemplateField, XgacFilter } from './types';

const resolveId = (value: unknown): string => {

  if (typeof value === 'string') return value;
  if (value && typeof value === 'object') {

    const obj = value as Record<string, unknown>;
    if (typeof obj.id === 'string') return obj.id;
    if (obj.$uuid) return String(obj.$uuid);
    if (obj._id) return String(obj._id);

  }
  return '';

};

const sanitizeFilterSection = (section: Record<string, string> | undefined) => {

  if (!section) return {};
  return Object.fromEntries(
    Object.entries(section).filter(([key, value]) => (
      key.length > 0
      && !key.startsWith('__new__')
      && value !== ''
    )),
  );

};

const sanitizeXgacFilter = (filter: XgacFilter): XgacFilter => ({
  ...filter,
  request: sanitizeFilterSection(filter.request),
  result: sanitizeFilterSection(filter.result),
});

const normalizeXgacFilter = (meta: Record<string, unknown> | undefined): XgacFilter => {

  const fromXgac = meta?.xgacFilter as XgacFilter | undefined;
  const fromLegacy = meta?.filter as XgacFilter | undefined;
  return sanitizeXgacFilter(fromXgac || fromLegacy || { ...GET_ASSETS_DEFAULT_XGAC_FILTER });

};

const normalizeIfScript = (ifScript: ScheduleTaskForm['ifScript']) => {

  if (!ifScript?.name || ifScript.name === '') return null;
  if (ifScript.name === 'checkConfirmations') {

    return {
      name: 'checkConfirmations',
      meta: {
        requiredConfirmations: String(ifScript.meta?.requiredConfirmations ?? ''),
      },
    };

  }
  return null;

};

const normalizeMetaScript = (metaScript: ScheduleTaskForm['metaScript']) => {

  if (!metaScript?.name) return null;
  if (metaScript.name === 'getAssets') {

    const xgacFilter = sanitizeXgacFilter(normalizeXgacFilter(metaScript.meta));
    return {
      name: 'getAssets',
      meta: {
        filter: {},
        xgacFilter,
      },
    };

  }
  return {
    name: metaScript.name,
    meta: metaScript.meta || {},
  };

};

const normalizeScheduleTaskForSave = (task: ScheduleTaskForm) => {

  const taskTemplateId = resolveId(task.taskTemplate);
  const delay = typeof task.delaySeconds === 'number'
    ? task.delaySeconds * 1000
    : (task.delay ?? 0);

  return {
    id: resolveId(task.id) || uuidv4(),
    delay,
    taskTemplate: taskTemplateId,
    ifScript: normalizeIfScript(task.ifScript),
    metaScript: normalizeMetaScript(task.metaScript),
    metaData: task.metaData ?? null,
  };

};

export const transformProcessTemplateForSave = (data: Record<string, unknown>, isCreate = false) => {

  const result = { ...data };
  delete result.createdAt;
  delete result.updatedAt;
  delete result.customerId;
  if (isCreate) delete result.id;

  const scope = result.scope as string | undefined;
  delete result.scope;

  if (isCreate) {

    result.xgacCustomerId = scope === 'global' ? null : getCurrentCustomer()?.value ?? null;

  } else if ('xgacCustomerId' in data) {

    // Scope cannot change on edit: keep the stored owner so a global template is never
    // turned into a customer-specific one.
    result.xgacCustomerId = (data.xgacCustomerId ?? null) as string | null;

  } else {

    delete result.xgacCustomerId;

  }

  result.scheduleTasks = ((result.scheduleTasks as ScheduleTaskForm[]) || []).map(normalizeScheduleTaskForSave);
  return result;

};

export const transformProcessTemplateForForm = (record: Record<string, unknown>) => {

  const xgacCustomerId = record.xgacCustomerId as string | null | undefined;
  const scope = xgacCustomerId == null ? 'global' : 'customer';

  const scheduleTasks = ensureMinScheduleTasks(record.scheduleTasks as ScheduleTaskForm[]).map((task) => {

    const taskTemplateId = resolveId(task.taskTemplate);
    let metaScript = task.metaScript;
    if (metaScript?.name === 'getAssets' && metaScript.meta) {

      metaScript = {
        ...metaScript,
        meta: {
          ...metaScript.meta,
          xgacFilter: normalizeXgacFilter(metaScript.meta),
        },
      };

    }

    const resolvedId = resolveId(task.id) || uuidv4();

    return {
      ...task,
      id: resolvedId,
      delaySeconds: (task.delay ?? 0) / 1000,
      taskTemplate: taskTemplateId || null,
      ifScript: task.ifScript,
      metaScript,
      metaData: task.metaData ?? null,
    };

  });

  return {
    ...record,
    scope,
    scheduleTasks,
  };

};

export const transformProcessTemplateForClone = (record: Record<string, unknown>) => {

  const form = transformProcessTemplateForForm(record) as Record<string, unknown>;
  delete form.id;
  delete form._id;
  form.processName = `${form.processName} - Copy`;
  form.scheduleTasks = (form.scheduleTasks as ScheduleTaskForm[]).map((task) => ({
    ...JSON.parse(JSON.stringify(task)),
    id: uuidv4(),
  }));

  if (record.xgacCustomerId != null) {

    form.scope = 'customer';

  }

  return form;

};

export const buildMetaScriptForTaskTemplate = (
  taskTemplate: { inputMeta?: { metaScripts?: { name: string; fields: { name: string; empty?: unknown }[] }[] } },
) => {

  const metaScripts = taskTemplate.inputMeta?.metaScripts || [];
  const getAssetsScript = metaScripts.find((script) => script.name === 'getAssets');

  if (getAssetsScript) {

    const meta: Record<string, unknown> = {
      filter: {},
      xgacFilter: { ...GET_ASSETS_DEFAULT_XGAC_FILTER },
    };
    for (const field of getAssetsScript.fields || []) {

      if (field.name !== 'filter' && field.empty !== undefined) {

        meta[field.name] = field.empty;

      }

    }
    return { name: 'getAssets', meta };

  }

  const first = metaScripts[0];
  if (!first) return null;

  const meta: Record<string, unknown> = {};
  for (const field of first.fields || []) {

    meta[field.name] = field.empty ?? {};

  }
  return { name: first.name, meta };

};

export const getGetAssetsFilterField = (
  taskTemplate: { inputMeta?: { metaScripts?: { name: string; fields: TaskTemplateField[] }[] } } | null,
) => {

  const getAssetsScript = taskTemplate?.inputMeta?.metaScripts?.find((script) => script.name === 'getAssets');
  return getAssetsScript?.fields?.find((field) => field.name === 'filter') ?? null;

};
